import React, { useMemo, useState } from 'react';
import Table from 'react-bootstrap/Table';
import AppModal from '../ui/AppModal';
import ActionIcon from '../ui/ActionIcon';
import StatusBadge from '../ui/StatusBadge';
import StatusSelect from '../ui/StatusSelect';
import {
  getPersistedCandidateStatus,
  getStatusDropdownLabel,
} from '../../utils/candidateStatus';
import { formatLwdDisplay } from '../../utils/dateFormat';

const STATUS_FILTER_LEADING_OPTIONS = [
  { value: 'No Action Taken', label: getStatusDropdownLabel('No Action Taken') },
];

function RequirementCandidatesModal({
  show,
  onHide,
  title,
  subtitle,
  candidates = [],
  initialStatusFilter = '',
  showRecruiterColumn = true,
  onViewCandidate,
}) {
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');

  React.useEffect(() => {
    if (show) {
      setName('');
      setRole('');
      setStatus(initialStatusFilter || '');
    }
  }, [show, initialStatusFilter]);

  const filteredCandidates = useMemo(() => (
    candidates.filter((candidate) => {
      const fullName = `${candidate.firstName || ''} ${candidate.lastName || ''}`.trim().toLowerCase();
      const recentStatus = getPersistedCandidateStatus(candidate);
      const matchesName = name ? fullName.includes(name.toLowerCase()) : true;
      const matchesRole = role ? String(candidate.role || '').toLowerCase().includes(role.toLowerCase()) : true;
      const matchesStatus = status ? recentStatus === status : true;
      return matchesName && matchesRole && matchesStatus;
    })
  ), [candidates, name, role, status]);

  return (
    <AppModal
      show={show}
      onHide={onHide}
      title={title}
      subtitle={subtitle}
      size="xl"
      fullscreen="lg-down"
    >
      <div className="hr-requirement-profiles-toolbar">
        <input
          type="search"
          className="form-control"
          placeholder="Search by name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          type="search"
          className="form-control"
          placeholder="Search by role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
        />
        <StatusSelect
          className="hr-requirement-profiles-status-filter"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          placeholder="All Statuses"
          leadingOptions={STATUS_FILTER_LEADING_OPTIONS}
        />
      </div>

      <p className="hr-page-hint mb-3">
        {filteredCandidates.length} profile{filteredCandidates.length === 1 ? '' : 's'} shown.
      </p>

      {filteredCandidates.length === 0 ? (
        <p className="hr-empty-state">No source profiles match the current filters.</p>
      ) : (
        <div className="table-responsive">
          <Table bordered hover responsive className="hr-table table-sm">
            <thead>
              <tr>
                {showRecruiterColumn && <th>Employee Name</th>}
                <th>Source Profile Name</th>
                <th>Role</th>
                <th>Total YOE</th>
                <th>LWD</th>
                <th>ECTC</th>
                <th>Status</th>
                <th>Uploaded Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredCandidates.map((candidate, index) => {
                const recentStatus = getPersistedCandidateStatus(candidate);
                return (
                  <tr key={candidate._id || index}>
                    {showRecruiterColumn && <td>{candidate.recruiterName || '—'}</td>}
                    <td>{candidate.firstName} {candidate.lastName}</td>
                    <td>{candidate.role || '—'}</td>
                    <td>{candidate.totalYoe || '—'}</td>
                    <td>{formatLwdDisplay(candidate.lwd)}</td>
                    <td>{candidate.ectc || '—'}</td>
                    <td>
                      <StatusBadge status={recentStatus} useStoredLabel />
                    </td>
                    <td>
                      {candidate.uploadedOn
                        ? new Date(candidate.uploadedOn).toLocaleDateString()
                        : '—'}
                    </td>
                    <td>
                      <ActionIcon
                        variant="view"
                        src="/Images/view.svg"
                        alt="View candidate"
                        onClick={() => onViewCandidate?.(candidate._id)}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}
    </AppModal>
  );
}

export default RequirementCandidatesModal;
